import type { InsightCard, MetricComparisonResponse } from '../../../../data/ServerApiClient'
import { formatValue } from './format'
import { HIGHLIGHT_TEMPLATE_REGISTRY } from './registry'
import type { HighlightReviewBaseline, HighlightSelectionResult, HighlightVisualEvidence, HighlightVisualId, KeyHighlight } from './types'

export const VISUAL_SELECTION_VERSION = 'key-highlights-selection-v4'
export const VISUAL_EVIDENCE_SCHEMA_VERSION = 'key-highlights-evidence-v5'

/**
 * Comparisons that are only meaningful once the period has closed.
 *
 * A month-over-month move measured against a half-posted month is a
 * completeness artifact, not a business event: the recurring costs are in and
 * the revenue is not, so it always reads as a collapse. Cards carrying one of
 * these comparisons are ineligible while the period is open. Point-in-time
 * signals — an aging position, a liquidity ratio, a cash balance — stay
 * eligible, because they are true on any day of the month.
 */
const PERIOD_CLOSE_DEPENDENT_COMPARISONS = new Set(['mom', 'qoq', 'yoy', 'ytd', 'l3m_vs_prior3m', 'l12m_vs_prior12m'])

/**
 * P&L metrics that move together by construction. Revenue falling drags gross
 * profit and net income with it; publishing all three is one story printed
 * three times, and it crowds out the genuinely independent findings.
 * Highest-ranked member wins its slot and the rest are folded in as context.
 */
const IMPLIED_PL_METRICS = ['revenue', 'gross_profit', 'operating_expenses', 'net_income', 'ebitda', 'cost_of_sales']

/**
 * How much of two highlights' dominant evidence may coincide before they are
 * telling the reader the same thing.
 *
 * Two thirds is deliberately strict. The failure this guards against is not
 * subtle overlap — it is two panels built from the *identical* point array,
 * which is what shipped: five templates share `statementVariancePoints` today,
 * so `visual_family` and `visual_form` differed while the data did not.
 */
const MAX_EVIDENCE_OVERLAP = 0.66

/** How many of the largest points define "what this visual is about". */
const DOMINANT_POINT_COUNT = 4

/**
 * The point ids that carry a visual's meaning: the largest few by magnitude.
 *
 * Deliberately not the full point set — a bridge showing six drivers and a
 * ranked chart showing the top three of the same six are still the same finding,
 * and comparing full sets would let them both through.
 */
function dominantEvidenceKeys(evidence: HighlightVisualEvidence): Set<string> {
  return new Set(
    [...evidence.points]
      // Absolute opening/closing columns establish the bridge scale; they are
      // not drivers and must not dominate overlap or principal-movement tests.
      .filter((entry) => !entry.absolute && Number.isFinite(entry.rawValue) && entry.rawValue !== 0)
      .sort((a, b) => Math.abs(b.rawValue) - Math.abs(a.rawValue) || compareText(a.id, b.id))
      .slice(0, DOMINANT_POINT_COUNT)
      .map((entry) => entry.id),
  )
}

/** Jaccard overlap of two dominant sets. 1 = identical, 0 = disjoint. */
function evidenceOverlap(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0
  let shared = 0
  for (const key of a) if (b.has(key)) shared += 1
  return shared / (a.size + b.size - shared)
}

const severityWeight: Record<InsightCard['severity'], number> = { bad: 4, check: 3, watch: 2, good: 1 }
const clean = (value: unknown) => String(value ?? '').trim()
const normalized = (value: unknown) => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
const compareText = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0
const impactMagnitude = (card: InsightCard): number => {
  const value = Math.abs(Number(card.impact?.value) || 0)
  switch (card.impact?.unit) {
    case 'usd': return Math.log10(1 + value)
    case 'pct': case 'pct_pts': return value / 5
    case 'months': return value * 2
    case 'ratio': return value * 3
    default: return value
  }
}

const priorityMetricCategory: Record<string, string> = {
  revenue: 'revenue',
  net_income: 'profitability',
  gross_profit: 'profitability',
  operating_expenses: 'expense',
}

/**
 * A quiet detector set must not make the live briefing disappear. Supplement
 * it with deterministic, governed month-over-month priorities from the same
 * canonical comparison payload. These are not synthetic values or AI prose:
 * every value, trend point, period, and drill target comes from the served
 * comparison contract, and the selector still rejects candidates without
 * valid visual evidence.
 */
export function withCurrentPriorityCandidates(
  existing: InsightCard[],
  comparisons?: MetricComparisonResponse | null,
  targetCount = 3,
  options: { periodOpen?: boolean } = {},
): InsightCard[] {
  if (!comparisons || existing.length >= targetCount) return existing
  // While the month is still posting, a MoM move measured against it is an
  // artifact of what has not booked yet. Rather than leave the briefing empty
  // — which is what shipped, and which made a correct refusal look identical
  // to a broken page — step back one period and compare the last CLOSED month
  // to the one before it. Same arithmetic, complete data on both sides.
  const closedShift = options.periodOpen ? 1 : 0
  const cards = [...existing]
  const represented = new Set(existing.map((card) => normalized(card.impact?.metric_id)))
  // One P&L movement only. These four metrics are arithmetically entangled, so
  // adding several fills the briefing with restatements of a single event.
  const alreadyHasPlMovement = existing.some((card) => IMPLIED_PL_METRICS.includes(normalized(card.impact?.metric_id)))
  const metricOrder = alreadyHasPlMovement ? [] : ['net_income', 'revenue', 'operating_expenses', 'gross_profit']
  for (const metricId of metricOrder) {
    if (cards.length >= targetCount || represented.has(metricId)) continue
    const row = comparisons.metrics.find((item) => normalized(item.id) === metricId)
    const comparison = row?.comparisons.find((item) => item.kind === 'mom')
    // The served comparison is anchored to the latest period. When that period
    // is open we re-derive the same MoM one month back from the trend series,
    // which carries a value per period and is therefore shiftable.
    const trendPoints = (row?.trend || []).filter((item) => Number.isFinite(Number(item.value)))
    const shifted = closedShift > 0 && trendPoints.length >= closedShift + 2
    const current = shifted
      ? Number(trendPoints[trendPoints.length - 1 - closedShift].value)
      : Number(comparison?.current_value)
    const prior = shifted
      ? Number(trendPoints[trendPoints.length - 2 - closedShift].value)
      : Number(comparison?.prior_value)
    const delta = shifted ? current - prior : Number(comparison?.delta_abs)
    if (closedShift > 0 && !shifted) continue
    if (!row || !Number.isFinite(delta) || delta === 0 || !Number.isFinite(current) || !Number.isFinite(prior)) continue
    const lowerIsBetter = metricId === 'operating_expenses'
    const favorable = lowerIsBetter ? delta < 0 : delta > 0
    const direction = delta > 0 ? 'up' : 'down'
    const pct = Number(comparison?.delta_pct)
    const pctText = Number.isFinite(pct) ? formatValue(Math.abs(pct * 100), 'pct') : 'a material amount'
    const verb = delta > 0 ? 'increased' : 'decreased'
    const amount = formatValue(Math.abs(delta), 'usd')
    const rank = cards.length + 1
    cards.push({
      id: `current_priority:${metricId}:mom`,
      detector: 'period_variance',
      severity: favorable ? 'good' : 'watch',
      headline: `${row.label} ${verb} ${pctText} since last month`,
      body: `${row.label} moved by ${formatValue(delta, 'usd', { signed: true })}, from ${formatValue(prior, 'usd')} to ${formatValue(current, 'usd')}.`,
      impact: { value: delta, unit: 'usd', metric_id: metricId, comparison: 'mom', direction },
      drill_target: { tab: 'financials', view: 'profit_and_loss', params: { metric: metricId, comparison: 'mom' } },
      score: Math.max(Math.abs(Number.isFinite(pct) ? pct : 0), Math.log10(1 + Math.abs(delta)) / 10),
      rank,
      tags: [
        'current_priority',
        // Marks the card as measured through a CLOSED month, which is what
        // makes it eligible while the reporting period is still open.
        ...(shifted ? ['closed_period_anchor'] : []),
        ...(favorable ? ['favorable', 'opportunity'] : []),
        ...(favorable ? [] : ['watch']),
      ],
      category: priorityMetricCategory[metricId],
      trend: (row.trend || []).filter((item) => Number.isFinite(Number(item.value))).map((item) => ({ period: item.period, value: Number(item.value) })),
      value_display: formatValue(delta, 'usd', { signed: true }),
    })
    represented.add(metricId)
  }
  return cards
}

export function insightIsDataQuality(card: InsightCard): boolean {
  if (card.explain?.is_data_quality) return true
  const terms = [card.category, card.explain?.category, card.detector, ...(card.tags || [])].map(normalized)
  return terms.some((term) => /bookkeep|data_quality|reconcil|close_control|opening_balance|missing_document|trust/.test(term))
}

function eligibleTemplates(card: InsightCard, evidence: HighlightVisualEvidence[]) {
  const category = normalized(card.category || card.explain?.category)
  const detector = normalized(card.detector)
  return HIGHLIGHT_TEMPLATE_REGISTRY.filter((candidate) => {
    const categoryMatch = candidate.eligibleCategories.some((item) => category.includes(normalized(item)))
    const detectorMatch = candidate.eligibleDetectors.some((item) => detector === normalized(item) || detector.includes(normalized(item)))
    return categoryMatch || detectorMatch
  }).map((candidate) => {
    const item = evidence.find((entry) => entry.id === `${card.id}:${candidate.id}` && entry.kind === candidate.evidenceKind)
    const pointRatio = item ? Math.min(1, item.points.length / Math.max(1, candidate.minimumPoints)) : 0
    const periodRatio = item ? Math.min(1, new Set(item.periods).size / Math.max(1, candidate.minimumPeriods)) : 0
    return { template: candidate, evidence: item, completeness: (pointRatio + periodRatio) / 2 }
  })
}

function storyFingerprint(card: InsightCard): string {
  return `${normalized(card.detector)}:${normalized(card.impact?.metric_id || card.id)}`
}

function changeState(card: InsightCard, prior: KeyHighlight | undefined, hasBaseline: boolean): KeyHighlight['changeState'] {
  const tags = (card.tags || []).map(normalized)
  if (tags.includes('resolved')) return 'resolved'
  if (!hasBaseline) return 'current_priority'
  if (!prior) return card.severity === 'good' ? 'improved' : 'new'
  const priorWeight = severityWeight[prior.severity] || 1
  const currentWeight = severityWeight[card.severity] || 1
  if (card.severity === 'good' && prior.severity !== 'good') return 'resolved'
  if (currentWeight > priorWeight) return 'worsened'
  if (currentWeight < priorWeight) return 'improved'
  const currentImpact = Math.abs(Number(card.impact?.value) || 0)
  const priorImpact = Math.abs(Number(prior.impactValue) || 0)
  if (priorImpact > 0 && currentImpact > priorImpact * 1.15) return 'worsened'
  if (priorImpact > 0 && currentImpact < priorImpact * .85) return 'improved'
  return 'still_priority'
}

/**
 * A sentence about what actually moved, built from the evidence.
 *
 * This replaces a three-branch template keyed on severity alone, which could
 * only ever emit variations of "assign an owner to validate revenue" — text
 * with no access to drivers, amounts, accounts or dates, and therefore no
 * ability to say anything. A board reader learns nothing from it and stops
 * reading the block.
 *
 * Returns null when the evidence does not support a specific statement. An
 * empty finding renders as no block at all, which is the honest outcome:
 * prescriptive advice belongs to the advisor, through the report commentary
 * pipeline, not to a string template in the renderer.
 */
function principalFinding(evidenceItem: HighlightVisualEvidence, impactValue: number): string | null {
  const ranked = [...evidenceItem.points]
    .filter((entry) => !entry.absolute && Number.isFinite(entry.rawValue) && entry.rawValue !== 0 && !entry.id.endsWith(':all_other'))
    .sort((a, b) => Math.abs(b.rawValue) - Math.abs(a.rawValue) || compareText(a.id, b.id))
  const top = ranked[0]
  if (!top || !clean(top.label)) return null

  // P&L bridge steps are effects on profit, not the raw line-item direction.
  // A cost decline is therefore a positive bridge contribution. Describing
  // that contribution with the generic "rose" verb says COGS rose when it
  // actually fell—the exact opposite of the governed statement movement.
  if (evidenceItem.sourceLineage.includes('pnl_bridge')) {
    const effects = ranked.slice(0, 2).map((entry) =>
      `${entry.label} ${formatValue(entry.rawValue, entry.unit || evidenceItem.unit, { signed: true })}`,
    )
    return `Largest profit effects: ${effects.join('; ')}.`
  }

  // Describe the driver with a verb and a magnitude, not a signed number
  // beside another signed number. "Wages accounts for +$52.1K of the −$73.0K
  // move" reads as a contradiction; wages rising is exactly what makes net
  // income fall, and the sentence should say so.
  const verb = top.rawValue > 0 ? 'rose' : 'fell'
  const magnitude = formatValue(Math.abs(top.rawValue), top.unit || evidenceItem.unit)
  const total = Math.abs(impactValue)
  const share = total > 0 ? Math.abs(top.rawValue) / total : null

  // Only claim a share when the driver genuinely sits inside the movement. A
  // driver larger than the total means the two are measuring different things,
  // and asserting "112% of the move" destroys trust in the whole card.
  if (share != null && share <= 1.02) {
    const totalText = formatValue(total, evidenceItem.unit)
    const weight = share >= 0.5 ? 'over half of' : share >= 0.25 ? 'about a quarter of' : 'part of'
    return `${top.label} ${verb} ${magnitude} — ${weight} the ${totalText} move.`
  }

  const second = ranked[1]
  if (second && clean(second.label)) {
    const secondVerb = second.rawValue > 0 ? 'rose' : 'fell'
    return `Largest movements: ${top.label} ${verb} ${magnitude}, ${second.label} ${secondVerb} ${formatValue(Math.abs(second.rawValue), second.unit || evidenceItem.unit)}.`
  }
  return `Largest movement: ${top.label} ${verb} ${magnitude}.`
}

function candidateTier(card: InsightCard, prior: KeyHighlight | undefined, hasBaseline: boolean): number {
  const state = changeState(card, prior, hasBaseline)
  if ((card.severity === 'bad' || card.severity === 'check') && ['new', 'worsened'].includes(state)) return 1
  if (card.severity === 'bad' || card.severity === 'check') return 2
  if (card.severity === 'watch' && state !== 'current_priority') return 3
  if (card.severity === 'good' && state === 'resolved') return 4
  return 5
}

function isMaterial(card: InsightCard, prior: KeyHighlight | undefined): boolean {
  if (!Number.isFinite(Number(card.impact?.value)) || Math.abs(Number(card.impact?.value)) === 0) return false
  if (card.severity !== 'good') return Number(card.score || 0) > 0
  const tags = (card.tags || []).map(normalized)
  return Boolean(prior && prior.severity !== 'good') || tags.some((tag) => /opportun|favorable|resolved|improved/.test(tag))
}

export function selectKeyHighlights(args: {
  insights: InsightCard[]
  evidence: HighlightVisualEvidence[]
  previous?: KeyHighlight[]
  baseline?: HighlightReviewBaseline | null
  limit?: number
  /** True when the reporting period has not closed. See `reportingPeriodState`. */
  periodOpen?: boolean
  /**
   * Detectors this surface suppresses. Expressed here rather than by filtering
   * the card list upstream, so suppression is one rule the selector can report
   * on, not a filter in one file and a re-derived attention count in another.
   */
  hiddenDetectors?: readonly string[]
}): HighlightSelectionResult {
  const rejections: HighlightSelectionResult['rejections'] = []
  // Tracks P&L metrics already represented, so arithmetically-entangled
  // siblings cannot each take a slot.
  const impliedMetricsUsed = new Set<string>()
  const baselineHighlights = args.baseline?.highlights || []
  const previous = args.previous?.length ? args.previous : baselineHighlights
  const previousByStory = new Map(previous.map((entry) => [entry.storyFingerprint, entry]))
  const previousOrder = new Map(previous.map((entry, index) => [entry.storyFingerprint, index]))
  const hasBaseline = Boolean(args.baseline && args.baseline.kind !== 'none')
  const ordered = [...args.insights].sort((a, b) => {
    const aPrior = previousByStory.get(storyFingerprint(a)); const bPrior = previousByStory.get(storyFingerprint(b))
    const aTier = candidateTier(a, aPrior, hasBaseline); const bTier = candidateTier(b, bPrior, hasBaseline)
    if (aPrior && bPrior && aTier === bTier) {
      const stable = (previousOrder.get(storyFingerprint(a)) || 0) - (previousOrder.get(storyFingerprint(b)) || 0)
      if (stable) return stable
    }
    if (aPrior && !bPrior && aTier === bTier) {
      const entryThreshold = Math.abs(Number(a.impact?.value) || 0) * 1.25
      if (impactMagnitude(b) <= impactMagnitude(a) || Math.abs(Number(b.impact?.value) || 0) <= entryThreshold) return -1
    }
    if (!aPrior && bPrior && aTier === bTier) {
      const entryThreshold = Math.abs(Number(b.impact?.value) || 0) * 1.25
      if (impactMagnitude(a) <= impactMagnitude(b) || Math.abs(Number(a.impact?.value) || 0) <= entryThreshold) return 1
    }
    return aTier - bTier
      || impactMagnitude(b) - impactMagnitude(a)
      || severityWeight[b.severity] - severityWeight[a.severity]
      || Number(b.score || 0) - Number(a.score || 0)
      || Number(a.rank || 0) - Number(b.rank || 0)
      || compareText(a.id, b.id)
  })
  const highlights: KeyHighlight[] = []
  const families = new Set<string>()
  const forms = new Set<string>()
  const selectedEvidenceKeys: Array<Set<string>> = []
  const hidden = new Set((args.hiddenDetectors || []).map(normalized))
  for (const card of ordered) {
    if (highlights.length >= Math.max(1, Math.min(3, args.limit ?? 3))) break
    if (hidden.has(normalized(card.detector))) {
      rejections.push({ insightId: card.id, reason: 'detector_hidden' })
      continue
    }
    if (insightIsDataQuality(card)) {
      rejections.push({ insightId: card.id, reason: 'data_quality' })
      continue
    }
    const comparison = normalized(card.impact?.comparison)
    // A card explicitly anchored to a closed month is measured on complete data
    // on both sides, so the open-period rule does not apply to it. Without this
    // exemption the fallback candidates are generated and then immediately
    // rejected, which is the bug that emptied every board pack mid-month.
    const closedAnchored = (card.tags || []).includes('closed_period_anchor')
    if (args.periodOpen && !closedAnchored && PERIOD_CLOSE_DEPENDENT_COMPARISONS.has(comparison)) {
      // The month is still posting. A period-over-period move measured now is
      // an artifact of what has not been booked yet, not a finding.
      rejections.push({ insightId: card.id, reason: 'open_period' })
      continue
    }
    const impliedMetric = normalized(card.impact?.metric_id)
    if (IMPLIED_PL_METRICS.includes(impliedMetric) && impliedMetricsUsed.size > 0) {
      // Revenue, gross profit and net income move together by construction.
      // The highest-ranked one already has the slot; the rest are the same
      // story and belong in its supporting detail, not in their own panel.
      rejections.push({ insightId: card.id, reason: 'implied_by_selected' })
      continue
    }
    const fingerprint = storyFingerprint(card)
    const prior = previousByStory.get(fingerprint)
    if (!isMaterial(card, prior)) {
      rejections.push({ insightId: card.id, reason: 'not_material' })
      continue
    }
    const candidates = eligibleTemplates(card, args.evidence).sort((a, b) => {
      const priorEvidence = prior ? args.evidence.find((entry) => entry.id === `${card.id}:${prior.templateId}`) : undefined
      const currentPrincipal = priorEvidence?.points
        .filter((entry) => !entry.absolute)
        .slice()
        .sort((left, right) => Math.abs(right.value) - Math.abs(left.value) || compareText(left.id, right.id))[0]?.id
      const driverChanged = Boolean(prior?.principalDriver && currentPrincipal && prior.principalDriver !== currentPrincipal)
      if (!driverChanged && a.template.id === prior?.templateId) return -1
      if (!driverChanged && b.template.id === prior?.templateId) return 1
      if (driverChanged && a.template.id === prior?.templateId) return 1
      if (driverChanged && b.template.id === prior?.templateId) return -1
      return b.completeness - a.completeness || HIGHLIGHT_TEMPLATE_REGISTRY.indexOf(a.template) - HIGHLIGHT_TEMPLATE_REGISTRY.indexOf(b.template)
    })
    if (!candidates.length) {
      rejections.push({ insightId: card.id, reason: 'no_evidence' })
      continue
    }
    let selected: typeof candidates[number] | null = null
    for (const candidate of candidates) {
      if (!candidate.evidence || candidate.evidence.points.length < candidate.template.minimumPoints || new Set(candidate.evidence.periods).size < candidate.template.minimumPeriods) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'minimum_evidence' })
        continue
      }
      // Having enough points is not the same as having a signal. A shape
      // mismatch upstream yields the right number of points with every value
      // at zero, and the row count alone was enough to promote it — so the pack
      // rendered an empty chart with a full set of axis labels. An empty frame
      // on a board pack is worse than one fewer highlight.
      if (!candidate.evidence.points.some((entry) => Number.isFinite(entry.rawValue) && entry.rawValue !== 0)) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'no_signal' })
        continue
      }
      // The unit gate, now that templates declare real units rather than all of
      // them. A months-valued runway figure has no business in a bridge whose
      // axis is dollars.
      const evidenceUnit = normalized(candidate.evidence.unit)
      if (evidenceUnit && !candidate.template.units.some((unit) => normalized(unit) === evidenceUnit)) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'unit_unsupported' })
        continue
      }
      if (families.has(candidate.template.visualFamily)) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'duplicate_family' })
        continue
      }
      if (forms.has(candidate.template.visualForm)) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'duplicate_form' })
        continue
      }
      // Distinct metadata is not distinct information. Two templates fed the
      // same evidence array pass the family and form checks and then draw the
      // reader the same picture twice — which is exactly what shipped.
      const candidateKeys = dominantEvidenceKeys(candidate.evidence)
      const collision = selectedEvidenceKeys.find((keys) => evidenceOverlap(keys, candidateKeys) > MAX_EVIDENCE_OVERLAP)
      if (collision) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'duplicate_evidence' })
        continue
      }
      if (!candidate.template.validDrillTabs.includes(clean(card.drill_target?.tab))) {
        rejections.push({ insightId: card.id, templateId: candidate.template.id, reason: 'unsupported_drill' })
        continue
      }
      selected = candidate
      break
    }
    if (!selected?.evidence) continue
    families.add(selected.template.visualFamily)
    forms.add(selected.template.visualForm)
    selectedEvidenceKeys.push(dominantEvidenceKeys(selected.evidence))
    if (IMPLIED_PL_METRICS.includes(impliedMetric)) impliedMetricsUsed.add(impliedMetric)
    const evidenceItem = selected.evidence
    const principal = evidenceItem.points
      .filter((entry) => !entry.absolute)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || compareText(a.id, b.id))[0]
    const principalDriver = principal?.id || normalized(card.impact?.metric_id || card.id)
    const impactValue = Number(card.impact?.value) || 0
    const impactUnit = clean(card.impact?.unit) || 'usd'
    highlights.push({
      id: `highlight:${card.id}`,
      insightId: card.id,
      storyFingerprint: fingerprint,
      severity: card.severity,
      canonicalRank: card.rank,
      changeState: changeState(card, prior, hasBaseline),
      // Never stringify a raw float into the largest type on the page.
      // `value_display` is only populated by some detectors, so the fallback
      // has to be the unit-aware formatter, not String().
      valueDisplay: clean(card.value_display) || formatValue(impactValue, impactUnit, { signed: true }),
      headline: clean(card.headline),
      implication: clean(card.body),
      principalFinding: principalFinding(evidenceItem, impactValue),
      drillTarget: card.drill_target,
      templateId: selected.template.id,
      visualFamily: selected.template.visualFamily,
      visualForm: selected.template.visualForm,
      evidenceRef: evidenceItem.id,
      evidenceHash: evidenceItem.hash,
      impactValue: Number(card.impact?.value) || 0,
      impactUnit: clean(card.impact?.unit),
      principalDriver,
      dataThrough: evidenceItem.dataThrough,
      provenanceState: evidenceItem.provenanceState,
      accessibilitySummary: [
        `${card.headline}.`,
        clean(card.body),
        principalFinding(evidenceItem, impactValue),
        `Data through ${evidenceItem.dataThrough || 'the latest available period'}; ${evidenceItem.freshness}; ${evidenceItem.provenanceState}.`,
      ].filter(Boolean).join(' '),
    })
  }
  return { highlights, rejections, visualSelectionVersion: VISUAL_SELECTION_VERSION, visualEvidenceSchemaVersion: VISUAL_EVIDENCE_SCHEMA_VERSION }
}

export function templateId(value: string): HighlightVisualId | null {
  return HIGHLIGHT_TEMPLATE_REGISTRY.some((entry) => entry.id === value) ? value as HighlightVisualId : null
}
